import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Container,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  useTheme,
  Paper,
} from "@mui/material";
import { LockOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import { motion } from "framer-motion";
import styled from "@emotion/styled";
import loginCover from "../../assets/login-cover.svg";
import { API_ENDPOINT } from "../../utils/constants";
import { saveToken, toastError } from "../../utils/util";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import RegisterPrompt from "./RegisterPrompt";
import { UserContext } from "../../context/UserProvider";

// Meteor background component with increased meteor count
const MeteorBackground = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  zIndex: -1,
  overflow: "hidden",
  background: "radial-gradient(ellipse at bottom, #1B2735 0%, #090A0F 100%)",
});

const Meteor = styled(motion.div)({
  position: "absolute",
  background:
    "linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,0) 100%)",
  borderRadius: "50%",
  boxShadow: "0 0 10px 1px rgba(255,255,255,0.5)",
});

// Login banner component
const LoginBanner = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  padding: theme.spacing(6),
  color: theme.palette.common.white,
  height: "100%",
  opacity: 0.9,
  background:
    "linear-gradient(135deg, rgba(16, 24, 32, 0.9) 0%, rgba(5, 46, 22, 0.9) 100%)",
  borderRadius: "16px",
  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.4)",
  position: "relative",
  overflow: "hidden",
  backdropFilter: "blur(4px)",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `url(${loginCover})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    opacity: 0.3, // Lower opacity for better text contrast
    zIndex: -1,
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  opacity: 0.8,
  flexDirection: "column",
  alignItems: "center",
  padding: theme.spacing(4),
  backgroundColor: "rgba(30, 30, 46, 0.85)",
  backdropFilter: "blur(10px)",
  borderRadius: "16px",
  boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
  border: "1px solid rgba(255,255,255,0.1)",
  height: "100%",
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(3),
  },
}));

const LoginForm = () => {
  const theme = useTheme();
  const { user, refreshUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [meteors, setMeteors] = useState([]);

  useEffect(() => {
    // Create 50 meteors for a more intense effect
    const newMeteors = Array.from({ length: 50 }).map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * -50, // Start above the viewport
      delay: Math.random() * 10,
      duration: 1 + Math.random() * 2, // Faster meteors
      size: Math.random() * 3 + 1,
      length: Math.random() * 150 + 50, // Longer trails
    }));
    setMeteors(newMeteors);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      const { data } = await axios.post(`${API_ENDPOINT}/user/login`, {
        email: email.trim(),
        password,
        rememberMe,
      });

      saveToken(data.token);
      refreshUser();
      navigate("/home");
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <MeteorBackground>
        {meteors.map((meteor) => (
          <Meteor
            key={meteor.id}
            initial={{
              x: `${meteor.x}vw`,
              y: `${meteor.y}vh`,
              opacity: 0,
            }}
            animate={{
              y: `${meteor.y + 150}vh`, // Longer travel distance
              x: `${meteor.x + (Math.random() - 0.5) * 30}vw`, // More horizontal variation
              opacity: [0, 1, 0],
            }}
            transition={{
              delay: meteor.delay,
              duration: meteor.duration,
              repeat: Infinity,
              repeatDelay: 2 + Math.random() * 5, // More frequent meteors
              ease: "linear",
            }}
            style={{
              width: `${meteor.size}px`,
              height: `${meteor.length}px`,
            }}
          />
        ))}
      </MeteorBackground>

      <Container
        maxWidth="lg"
        sx={{ height: "100vh", display: "flex", alignItems: "center" }}
      >
        <Grid container spacing={4} sx={{ height: "80vh" }}>
          {/* Left Banner */}
          <Grid item xs={12} md={6}>
            <LoginBanner>
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    mb: 2,
                    letterSpacing: "-0.03em",
                    lineHeight: 1.2,
                    background:
                      "linear-gradient(90deg, #d4af37 0%, #a3b18a 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    textFillColor: "transparent",
                    textShadow: "0 2px 10px rgba(0,0,0,0.3)",
                  }}
                >
                  Welcome Back
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 500,
                    mb: 3,
                    color: "rgba(255,255,255,0.9)",
                    letterSpacing: "0.01em",
                    textShadow: "0 1px 4px rgba(0,0,0,0.2)",
                  }}
                >
                  Sign in to access your dashboard
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    maxWidth: "80%",
                    fontSize: "1.1rem",
                    lineHeight: 1.6,
                    color: "rgba(255,255,255,0.8)",
                    textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  }}
                >
                  Manage your account, view analytics, and connect with your
                  team.
                </Typography>
              </motion.div>
            </LoginBanner>
          </Grid>

          {/* Right Form */}
          <Grid item xs={12} md={6}>
            <FormContainer
              sx={{
                backgroundColor: "rgba(25, 25, 40, 0.95)", // Darker background for better contrast
                border: "1px solid rgba(255,255,255,0.15)", // More visible border
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)", // Deeper shadow
              }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <Box textAlign="center" mb={4}>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <LockOutlined
                      sx={{
                        fontSize: 48,
                        color: "#BB86FC", // Brighter purple
                        backgroundColor: "rgba(187, 134, 252, 0.15)", // More visible glow
                        padding: theme.spacing(2),
                        borderRadius: "50%",
                        border: "2px solid rgba(187, 134, 252, 0.4)", // More visible border
                        mb: 2,
                        filter: "drop-shadow(0 0 8px rgba(187, 134, 252, 0.3))", // Added glow effect
                      }}
                    />
                  </motion.div>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700, // Bolder font
                      color: "#FFFFFF", // Pure white
                      textShadow: "0 1px 2px rgba(0,0,0,0.3)", // Better readability
                    }}
                  >
                    Account Login
                  </Typography>
                </Box>

                <Box component="form" onSubmit={handleSubmit} width="100%">
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="email"
                    label="Email Address"
                    name="email"
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    variant="filled"
                    sx={{
                      mb: 2,
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255,255,255,0.08)", // Brighter background
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.12)", // More visible hover
                        },
                        "&.Mui-focused": {
                          backgroundColor: "rgba(255,255,255,0.15)", // Focus state
                          border: "1px solid rgba(187, 134, 252, 0.5)", // Focus border
                        },
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        color: "rgba(255,255,255,0.75)", // Brighter label
                        "&.Mui-focused": {
                          color: "#BB86FC", // Purple when focused
                        },
                      },
                    }}
                    InputProps={{
                      sx: {
                        color: "#FFFFFF", // Pure white text
                        "&::before": {
                          borderBottom: "none", // Remove default underline
                        },
                      },
                    }}
                  />

                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    name="password"
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    variant="filled"
                    sx={{
                      mb: 1,
                      "& .MuiFilledInput-root": {
                        backgroundColor: "rgba(255,255,255,0.08)", // Brighter background
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: "rgba(255,255,255,0.12)", // More visible hover
                        },
                        "&.Mui-focused": {
                          backgroundColor: "rgba(255,255,255,0.15)", // Focus state
                          border: "1px solid rgba(187, 134, 252, 0.5)", // Focus border
                        },
                      },
                    }}
                    InputLabelProps={{
                      sx: {
                        color: "rgba(255,255,255,0.75)", // Brighter label
                        "&.Mui-focused": {
                          color: "#BB86FC", // Purple when focused
                        },
                      },
                    }}
                    InputProps={{
                      sx: {
                        color: "#FFFFFF", // Pure white text
                        "&::before": {
                          borderBottom: "none", // Remove default underline
                        },
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            sx={{
                              color: "rgba(255,255,255,0.7)", // Brighter icon
                              "&:hover": {
                                color: "#BB86FC", // Purple on hover
                              },
                            }}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={rememberMe}
                          onChange={(e) => setRememberMe(e.target.checked)}
                          color="primary"
                          sx={{
                            color: "rgba(255,255,255,0.8)", // Brighter checkbox
                            "&.Mui-checked": {
                              color: "#BB86FC", // Brighter purple
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{
                            color: "rgba(255,255,255,0.85)", // Brighter text
                            fontWeight: 500, // Medium weight
                          }}
                        >
                          Remember me
                        </Typography>
                      }
                    />
                    <Link
                      href="/reset-password"
                      variant="body2"
                      sx={{
                        color: "rgba(187, 134, 252, 0.9)", // Brighter purple
                        fontWeight: 500,
                        "&:hover": {
                          color: "#FFFFFF", // White on hover
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Forgot password?
                    </Link>
                  </Box>

                  <Button
                    type="submit"
                    fullWidth
                    disabled={loading}
                    variant="contained"
                    sx={{
                      py: 1.5,
                      borderRadius: "8px",
                      background:
                        "linear-gradient(90deg, #BB86FC 0%, #9C64F6 100%)",
                      color: "#FFFFFF",
                      fontWeight: 600,
                      fontSize: "1rem",
                      boxShadow: "0 4px 16px rgba(187, 134, 252, 0.4)", // More visible shadow
                      textTransform: "none",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 20px rgba(187, 134, 252, 0.6)", // More pronounced hover
                        background:
                          "linear-gradient(90deg, #9C64F6 0%, #BB86FC 100%)", // Reversed gradient on hover
                      },
                      "&:disabled": {
                        background: "rgba(255,255,255,0.1)",
                        color: "rgba(255,255,255,0.5)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {loading ? "Signing In..." : "Sign In"}
                  </Button>

                  <RegisterPrompt />
                </Box>
              </motion.div>
            </FormContainer>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default LoginForm;
